// TanStack Query hooks for the Gantt and people pages (constitution §5.4, plan §9). Lists are
// fresh for 30 s. The Gantt needs every bar at once, so it reads all pages of /gantt (100 a page,
// constitution §4.13): the first page says how many there are, the rest load in parallel.
import { queryOptions, useQuery } from '@tanstack/react-query';
import { createPlanningApi, type GanttPage, type QueryValues } from '@cis/api-client';

const LIST_STALE_MS = 30_000;
const MAX_PAGE_SIZE = 100;

export const planningApi = createPlanningApi();

export async function fetchWholeGantt(
  query: QueryValues,
  signal?: AbortSignal,
): Promise<GanttPage> {
  const first = await planningApi.gantt({ ...query, page: 1, pageSize: MAX_PAGE_SIZE }, signal);
  const pages = Math.ceil(first.total / MAX_PAGE_SIZE);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) =>
      planningApi.gantt({ ...query, page: i + 2, pageSize: MAX_PAGE_SIZE }, signal),
    ),
  );
  return { ...first, items: [first, ...rest].flatMap((p) => p.items) };
}

export const ganttQueryOptions = (query: QueryValues) =>
  queryOptions({
    queryKey: ['gantt', query],
    queryFn: ({ signal }) => fetchWholeGantt(query, signal),
    staleTime: LIST_STALE_MS,
  });

export const milestonesQueryOptions = queryOptions({
  queryKey: ['milestones'],
  queryFn: ({ signal }) => planningApi.milestones({ pageSize: MAX_PAGE_SIZE }, signal),
  staleTime: LIST_STALE_MS,
});

export const peopleQueryOptions = queryOptions({
  queryKey: ['people'],
  queryFn: ({ signal }) => planningApi.people({ pageSize: MAX_PAGE_SIZE }, signal),
  staleTime: LIST_STALE_MS,
});

export const personLoadQueryOptions = (id: number, weeks: number) =>
  queryOptions({
    queryKey: ['people', id, 'load', weeks],
    queryFn: ({ signal }) => planningApi.personLoad(id, { weeks }, signal),
    staleTime: LIST_STALE_MS,
  });

export const capacityQueryOptions = queryOptions({
  queryKey: ['leads', 'capacity'],
  queryFn: ({ signal }) => planningApi.capacity(signal),
  staleTime: LIST_STALE_MS,
});

export const useGantt = (query: QueryValues) => useQuery(ganttQueryOptions(query));
export const useMilestones = () => useQuery(milestonesQueryOptions);
export const usePeople = () => useQuery(peopleQueryOptions);
export const usePersonLoad = (id: number | undefined, weeks: number) =>
  useQuery({ ...personLoadQueryOptions(id ?? 0, weeks), enabled: id !== undefined });
export const useCapacity = (enabled: boolean) => useQuery({ ...capacityQueryOptions, enabled });
